import React, { useState, useEffect, useRef } from 'react';
import Icon from 'components/AppIcon';

const CATEGORY_ICONS = {
  TRADING: 'TrendingUp',
  BUSINESS: 'Building2',
  CRIME: 'Skull',
  FACTION: 'Users',
};

const CATEGORY_COLORS = {
  TRADING: 'text-blue-400',
  BUSINESS: 'text-green-400',
  CRIME: 'text-red-400',
  FACTION: 'text-purple-400',
};

const TYPE_COLORS = {
  DAILY: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  WEEKLY: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  SEASONAL: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

// Mini confetti burst
const ConfettiBurst = () => {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    color: ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#ec4899']?.[i % 5],
    angle: (i / 12) * 360,
    distance: 30 + Math.random() * 20,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg" style={{ zIndex: 10 }}>
      {particles?.map(p => (
        <div
          key={p?.id}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{
            background: p?.color,
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%)`,
            animation: `confetti-burst-${p?.id % 4} 0.8s ease-out forwards`,
            '--angle': `${p?.angle}deg`,
            '--dist': `${p?.distance}px`,
          }}
        />
      ))}
    </div>
  );
};

const QuestCard = ({ quest, playerQuest, onClaim, claiming, loading, isJustCompleted = false }) => {
  const [displayProgress, setDisplayProgress] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const [prevValue, setPrevValue] = useState(playerQuest?.current_value || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animFrameRef = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)')?.matches;

  const targetProgress = playerQuest
    ? Math.min((playerQuest?.current_value / quest?.target_value) * 100, 100)
    : 0;

  const isCompleted = playerQuest?.completed_at != null;
  const isClaimed = playerQuest?.claimed_at != null;
  const canClaim = isCompleted && !isClaimed;

  // Animate progress bar smoothly
  useEffect(() => {
    if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current);

    if (isMobile) {
      // Simple CSS transition on mobile for performance
      setDisplayProgress(targetProgress);
      return;
    }

    const start = displayProgress;
    const end = targetProgress;
    const duration = 600;
    const startTime = performance.now();

    const animate = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      // Ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplayProgress(start + (end - start) * eased);
      if (t < 1) animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);
    return () => { if (animFrameRef?.current) cancelAnimationFrame(animFrameRef?.current); };
  }, [targetProgress]);

  // Detect progress increase → flash animation
  useEffect(() => {
    const newValue = playerQuest?.current_value || 0;
    if (newValue > prevValue) {
      setIsAnimating(true);
      setPrevValue(newValue);
      setTimeout(() => setIsAnimating(false), 800);
    }
  }, [playerQuest?.current_value]);

  // Confetti on completion
  useEffect(() => {
    if (isJustCompleted || (isCompleted && !isClaimed && playerQuest?.completed_at)) {
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 1200);
    }
  }, [isJustCompleted, isCompleted]);

  const timeLeft = () => {
    if (!playerQuest?.expires_at) return null;
    const diff = new Date(playerQuest.expires_at) - new Date();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    return `${hours}h ${mins}m`;
  };

  return (
    <div
      className={`relative rounded-lg border p-4 transition-all duration-300 ${
        isClaimed
          ? 'bg-card/40 border-border/30 opacity-60'
          : isCompleted
          ? 'bg-green-500/5 border-green-500/30 shadow-sm'
          : isAnimating
          ? 'bg-primary/5 border-primary/40' :'bg-card border-border hover:border-primary/30'
      }`}
      style={{
        willChange: isAnimating ? 'transform' : 'auto',
        transform: isAnimating ? 'scale(1.01)' : 'scale(1)',
        transition: 'transform 0.2s ease, border-color 0.3s ease, background-color 0.3s ease',
      }}
    >
      {showConfetti && <ConfettiBurst />}

      {/* Completion glow overlay */}
      {isJustCompleted && (
        <div
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            background: 'radial-gradient(circle at center, rgba(34,197,94,0.15) 0%, transparent 70%)',
            animation: 'pulse 1s ease-out',
          }}
        />
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
              CATEGORY_COLORS?.[quest?.category] ? 'bg-primary/10' : 'bg-muted'
            }`}
          >
            <Icon
              name={CATEGORY_ICONS?.[quest?.category] || 'Star'}
              size={16}
              className={CATEGORY_COLORS?.[quest?.category] || 'text-muted-foreground'}
            />
          </div>
          <div className="min-w-0">
            <p className="font-medium text-sm text-foreground truncate">{quest?.quest_name}</p>
            <p className="text-xs text-muted-foreground truncate">{quest?.description}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span
            className={`text-xs px-2 py-0.5 rounded-full border font-medium ${
              TYPE_COLORS?.[quest?.quest_type] || 'bg-muted text-muted-foreground'
            }`}
          >
            {quest?.quest_type}
          </span>
          {timeLeft() && (
            <span className="text-xs text-muted-foreground">{timeLeft()}</span>
          )}
        </div>
      </div>

      {/* Progress */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-muted-foreground mb-1">
          <span className="flex items-center gap-1">
            Progress
            {isAnimating && (
              <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            )}
          </span>
          <span
            className={`font-mono transition-colors duration-300 ${
              isAnimating ? 'text-primary font-semibold' : ''
            }`}
          >
            {playerQuest?.current_value?.toLocaleString() || 0} /{' '}
            {quest?.target_value?.toLocaleString()}
          </span>
        </div>
        <div className="h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              isCompleted ? 'bg-green-500' : 'bg-primary'
            }`}
            style={{
              width: `${displayProgress}%`,
              transition: isMobile ? 'width 0.4s ease' : 'none',
              willChange: 'width',
              boxShadow: isAnimating ? '0 0 8px rgba(0,217,255,0.6)' : 'none',
            }}
          />
        </div>
        {/* Percentage label */}
        <div className="flex justify-end mt-0.5">
          <span className={`text-xs font-mono ${
            isCompleted ? 'text-green-400' : 'text-muted-foreground'
          }`}>
            {Math.round(displayProgress)}%
          </span>
        </div>
      </div>

      {/* Rewards + Claim */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Icon name="Zap" size={12} className="text-yellow-400" />
            <span className="text-xs text-yellow-400 font-medium">
              +{quest?.reward_xp?.toLocaleString()} XP
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Icon name="DollarSign" size={12} className="text-green-400" />
            <span className="text-xs text-green-400 font-medium">
              ${quest?.reward_cash?.toLocaleString()}
            </span>
          </div>
        </div>
        {canClaim && (
          <button
            onClick={() => onClaim?.(playerQuest?.id)}
            disabled={claiming === playerQuest?.id}
            className="px-3 py-1.5 rounded text-xs font-semibold bg-green-500 text-white hover:bg-green-400 transition-all duration-200 disabled:opacity-50 shadow-sm"
            style={{
              animation: canClaim ? 'pulse 2s infinite' : 'none',
              boxShadow: '0 0 12px rgba(34,197,94,0.4)',
            }}
          >
            {claiming === playerQuest?.id ? 'Claiming...' : '🎁 Claim Reward'}
          </button>
        )}
        {isClaimed && (
          <div className="flex items-center gap-1 text-xs text-green-400">
            <Icon name="CheckCircle" size={12} />
            <span>Claimed</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestCard;
